import { formatAddressParagraph } from "../../../common/components/AddressSuggestionsModal/address-suggestions-modal.util";
import { ORDER_DELIVERY_METHOD } from "../../../common/constants/order-display.constants";
import { deliveryMethodLabel } from "../../../common/helpers/order-display.util";
import { formatPhoneNumber } from "../../../common/helpers/phone-number.util";
import { formatPrice } from "../../../common/helpers/price.utils";
import { getString } from "../../../common/uistringlabels/ui-string-utils";
import { progressFor } from "../order-tracking-progress.util";
import type { OrderedItem, OrderTrackingView, SummaryRow } from "../order-tracking.types";
import { itoStatusLabelFor } from "../../../services/orders/order-display.mapper";
import type { Order, OrderItem } from "../../../services/orders/orders.types";

const SUMMARY_STRINGS = "orderTracking.summary";

const costDetails = (item: OrderItem | undefined): string[] => {
  const amount = item?.price?.amount;
  if (amount === undefined) {
    return [];
  }
  const cost = formatPrice(amount, item?.price?.currency, { free: getString("pricing.free") });
  return [getString("orderTracking.amznham.item.cost", { cost })];
};

const orderedItem = (item: OrderItem | undefined, imageUrl: string | undefined): OrderedItem => ({
  name: item?.itemInformation?.name ?? "",
  details: costDetails(item),
  imageUrl,
});

const rowIfPresent = (labelKey: string, value: string | undefined): SummaryRow[] =>
  value?.trim()
    ? [{ label: getString(`${SUMMARY_STRINGS}.${labelKey}`), value: value.trim() }]
    : [];

const formattedAddress = ({ deliveryTo }: Order): string | undefined =>
  deliveryTo
    ? formatAddressParagraph({
        line1: deliveryTo.addressLineData?.[0],
        line2: deliveryTo.addressLineData?.[1],
        line3: deliveryTo.addressLineData?.[2],
        city: deliveryTo.city,
        county: deliveryTo.county,
        state: deliveryTo.regionSubdivision1,
        zip: deliveryTo.postalCode,
        country: deliveryTo.country,
      })
    : undefined;

const recipientName = ({ deliveryTo, requester }: Order): string | undefined =>
  deliveryTo?.fullName ||
  [requester?.basicInfo?.firstName, requester?.basicInfo?.lastName].filter(Boolean).join(" ");

const orderDetailRows = (order: Order): SummaryRow[] => {
  const itemDetails = order.items?.[0]?.details;
  const checkoutInfo = order.details?.checkoutInfo;
  const returnedSerial = order.items?.find((item) => item.details?.returnedDeviceSerialNumber)
    ?.details?.returnedDeviceSerialNumber;
  return [
    ...rowIfPresent("replacedDeviceLabel", returnedSerial),
    ...rowIfPresent("costCenterLabel", checkoutInfo?.costCenter),
    ...rowIfPresent("carCodeLabel", checkoutInfo?.carCode),
    ...rowIfPresent("businessJustificationLabel", checkoutInfo?.businessJustification),
    ...rowIfPresent("poNumberLabel", itemDetails?.poNumber),
  ];
};

const shippingRows = (order: Order): SummaryRow[] => {
  const hopOrder = order.items?.[0]?.details?.hopOrders?.[0];
  const phoneNumber = order.deliveryTo?.phoneNumber;
  return [
    {
      label: getString(`${SUMMARY_STRINGS}.deliveryMethodLabel`),
      value: deliveryMethodLabel(ORDER_DELIVERY_METHOD.ship),
    },
    ...rowIfPresent("recipientLabel", recipientName(order)),
    ...rowIfPresent("addressLabel", formattedAddress(order)),
    ...rowIfPresent("phoneNumberLabel", phoneNumber && formatPhoneNumber(phoneNumber)),
    ...rowIfPresent("carrierLabel", hopOrder?.shippingCarrier),
    ...rowIfPresent("trackingNumberLabel", hopOrder?.trackingNumber),
    ...rowIfPresent("shippingOrderIdLabel", hopOrder?.identifier),
  ];
};

const withDividerAboveFirst = (rows: SummaryRow[]): SummaryRow[] =>
  rows.map((row, index) => (index === 0 ? { ...row, hasDividerAbove: true } : row));

/** The order details come first; the shipping details sit after their own divider when both exist. */
const summaryRowsFor = (order: Order): SummaryRow[] => {
  const orderRows = orderDetailRows(order);
  const shipping = shippingRows(order);
  return [...orderRows, ...(orderRows.length > 0 ? withDividerAboveFirst(shipping) : shipping)];
};

/** amznham orders are always shipped, and hold one item whose image only Catalogical has. */
export const toAmznhamTrackingView = (order: Order, imageUrl?: string): OrderTrackingView => ({
  orderNumber: order.orderReference ?? order.orderId,
  steps: progressFor(itoStatusLabelFor(order.status), ORDER_DELIVERY_METHOD.ship),
  item: orderedItem(order.items?.[0], imageUrl),
  summaryRows: summaryRowsFor(order),
});
